/**
 * AdminDebugPill - Floating admin-only debug toggle for legacy/canonical renderer
 * 
 * Appears as a small pill in the bottom-left corner of migrated pages.
 * Only visible to admins. Provides:
 * - Toggle to switch between canonical and legacy renderers
 * - Visual badge showing current mode + route
 * - Compare button to open opposite renderer in new tab
 * 
 * Console logs: [RendererDebug] route=<path> mode=canonical|legacy
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLegacyRendererDebug } from '../CanonicalRouteProvider';
import { 
  Bug, 
  ChevronDown, 
  ChevronUp, 
  Monitor, 
  MonitorOff,
  ExternalLink,
  X,
} from 'lucide-react';

const RENDERER_NAMES = {
  '/series/': 'SeriesPageRenderer',
  '/novel/': 'NovelPageRenderer',
  '/u/': 'CreatorPageRenderer',
  '/read/': 'ReaderPageRenderer',
};

const getRendererName = (pathname) => {
  for (const [prefix, name] of Object.entries(RENDERER_NAMES)) {
    if (pathname.startsWith(prefix)) return name;
  }
  return 'PageRenderer';
};

const AdminDebugPill = () => {
  const { legacyEnabled, isAdmin, toggle } = useLegacyRendererDebug();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);

  const rendererName = getRendererName(location.pathname);
  const mode = legacyEnabled ? 'legacy' : 'canonical';

  // Console log once per render cycle
  useEffect(() => {
    if (isAdmin) {
      console.info(`[RendererDebug] route=${location.pathname} mode=${mode}`);
    }
  }, [location.pathname, mode, isAdmin]);

  // Not admin → render nothing
  if (!isAdmin) return null;

  const handleCompare = () => {
    const oppositeMode = legacyEnabled ? 'canonical' : 'legacy';
    const url = `${location.pathname}${location.search ? location.search + '&' : '?'}debugRenderer=${oppositeMode}`;
    window.open(url, '_blank');
  };

  return (
    <>
      {/* Persistent badge when legacy is ON */}
      {legacyEnabled && (
        <div 
          className="fixed top-16 left-4 z-[9999] flex items-center gap-2 bg-red-500/90 text-white px-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm"
          data-testid="legacy-renderer-badge"
        >
          <MonitorOff className="w-3.5 h-3.5" />
          <span className="text-xs font-semibold">Legacy Renderer Enabled</span>
          <span className="text-xs opacity-75">| {location.pathname} | {rendererName}</span>
          <button 
            onClick={toggle} 
            className="ml-1 hover:bg-red-600 rounded-full p-0.5"
            data-testid="legacy-badge-dismiss"
            title="Switch to canonical"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Floating debug pill — bottom-left */}
      <div 
        className="fixed bottom-4 left-4 z-[9999]"
        data-testid="admin-debug-pill"
      >
        {/* Collapsed state: small bug icon */}
        {!expanded ? (
          <button
            onClick={() => setExpanded(true)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full shadow-lg transition-colors ${
              legacyEnabled 
                ? 'bg-red-500/90 text-white hover:bg-red-600' 
                : 'bg-zinc-800/90 text-zinc-400 hover:bg-zinc-700 hover:text-white'
            } backdrop-blur-sm border border-zinc-700/50`}
            data-testid="debug-pill-toggle"
            title="Debug: Renderer Toggle"
          >
            <Bug className="w-4 h-4" />
            <span className="text-xs font-medium">
              {legacyEnabled ? 'Legacy' : 'Canonical'}
            </span>
            <ChevronUp className="w-3 h-3" />
          </button>
        ) : (
          /* Expanded state: full debug panel */
          <div className="bg-zinc-900/95 border border-zinc-700 rounded-xl shadow-2xl backdrop-blur-sm w-64 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <Bug className="w-4 h-4 text-orange-500" />
                <span className="text-xs font-semibold text-white">Debug Panel</span>
              </div>
              <button 
                onClick={() => setExpanded(false)}
                className="text-zinc-500 hover:text-white"
                data-testid="debug-panel-close"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>

            {/* Info */}
            <div className="px-3 py-2 space-y-1 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Route</span>
                <span className="text-xs text-zinc-300 font-mono truncate max-w-[160px]">{location.pathname}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Renderer</span>
                <span className="text-xs text-zinc-300">{rendererName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Mode</span>
                <span className={`text-xs font-semibold ${legacyEnabled ? 'text-red-400' : 'text-green-400'}`}>
                  {legacyEnabled ? 'LEGACY' : 'CANONICAL'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="px-3 py-2 space-y-2">
              {/* Toggle */}
              <button
                onClick={toggle}
                data-testid="debug-legacy-toggle"
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  legacyEnabled 
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30' 
                    : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700'
                }`}
              >
                <span className="flex items-center gap-2">
                  {legacyEnabled ? <MonitorOff className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}
                  {legacyEnabled ? 'Use Legacy Renderer' : 'Use Legacy Renderer'}
                </span>
                <span className={`w-8 h-4 rounded-full relative transition-colors ${
                  legacyEnabled ? 'bg-red-500' : 'bg-zinc-600'
                }`}>
                  <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                    legacyEnabled ? 'left-[18px]' : 'left-0.5'
                  }`} />
                </span>
              </button>

              {/* Compare button */}
              <button
                onClick={handleCompare}
                data-testid="debug-compare-btn"
                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs text-zinc-400 hover:text-white hover:bg-zinc-800 border border-zinc-800 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open {legacyEnabled ? 'Canonical' : 'Legacy'} in new tab
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminDebugPill;
